import type { PersistStorage, StorageValue } from 'zustand/middleware/persist';
import {
  safeLocalStorage,
  safeSessionStorage
} from '../helpers/local-storage.helper';
import { createPersistedStore, type Store } from './store-creators';

const BROWSER_STORAGE_SELECTED_ORGANIZATION_ID =
  'selected-organization-id' as const;

type OrganizationStore = {
  selectedOrganizationId: string;
};

const storage: PersistStorage<Store<OrganizationStore>> = {
  getItem: (
    name: string
  ): StorageValue<Store<OrganizationStore>> | undefined => {
    const raw =
      safeSessionStorage.getItem(name) || safeLocalStorage.getItem(name);
    if (!raw) {
      return undefined;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return undefined;
    }
  },
  setItem: (name: string, value: StorageValue<Store<OrganizationStore>>) => {
    const serialized = JSON.stringify(value);
    const sessionStored = safeSessionStorage.setItem(name, serialized);
    const localStored = safeLocalStorage.setItem(name, serialized);

    // `getItem` prefers session over local, so a half-landed write leaves the
    // older value in the store that failed — and it would win on the next read,
    // silently scoping the app to the previously selected organization. Drop
    // whichever side did not take the new value rather than let it shadow.
    if (!sessionStored) {
      safeSessionStorage.removeItem(name);
    }
    if (!localStored) {
      safeLocalStorage.removeItem(name);
    }
  },
  removeItem: (name: string) => {
    safeSessionStorage.removeItem(name);
    safeLocalStorage.removeItem(name);
  }
};

/**
 * A simple store for retrieving and updating the selected organization for
 * the current user. This store is persisted in both session and local storage.
 */
export const useSelectedOrganizationStore =
  createPersistedStore<OrganizationStore>(
    () => ({
      selectedOrganizationId: undefined
    }),
    {
      name: BROWSER_STORAGE_SELECTED_ORGANIZATION_ID,
      storage
    }
  );
